// Shared report rendering (refusal branch vs findings branch + evidence + usage footer).
// Used by both ReportCard (live `report` event) and InvestigationDetailDialog (history detail).
// The detail path can carry null evidence / null latency / null usage; this component guards
// every nullable field and NEVER fabricates a fallback (no `?? 0`), per the no-fallback rule.
import { useTranslate } from 'react-admin';
import {
  Box,
  Alert,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { Storage as DbIcon, Article as LogIcon } from '@mui/icons-material';
import { CategoryChip, ConfidenceChip, refusedReasonLabel } from './reportDisplay';
import type {
  Confidence,
  EvidenceItem,
  RefusedReason,
  RootCauseCategory,
} from './types';

// Normalized props accepted from either the report event or the persisted detail.
export interface ReportBodyData {
  status: 'completed' | 'refused' | 'running' | 'failed';
  refusedReason: RefusedReason | null;
  rootCauseCategory: RootCauseCategory | null;
  confidence: Confidence | null;
  conclusionSummary: string | null;
  evidence: EvidenceItem[] | null;
  model: { provider: string | null; name: string | null };
  usage: { inputTokens: number | null; outputTokens: number | null; costUsd: number | null } | null;
  latencyMs: number | null;
}

const numOrDash = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : String(n);

const costOrDash = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `$${n.toFixed(4)}`;

export const ReportBody = ({ data }: { data: ReportBodyData }) => {
  const translate = useTranslate();

  // Refusal branch — no category/confidence/evidence/handoff (all null per REF-05 s.3).
  if (data.status === 'refused') {
    const reasonText = data.refusedReason
      ? refusedReasonLabel(translate, data.refusedReason)
      : translate('aiTroubleshooting.report.refusedGeneric');
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {translate('aiTroubleshooting.report.refusedTitle')}
        </Typography>
        <Typography variant="body2">{reasonText}</Typography>
      </Alert>
    );
  }

  const evidence = data.evidence ?? [];

  return (
    <Box sx={{ mt: 1 }}>
      {/* Category + confidence chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        {data.rootCauseCategory && (
          <CategoryChip translate={translate} category={data.rootCauseCategory} />
        )}
        {data.confidence && <ConfidenceChip translate={translate} confidence={data.confidence} />}
      </Box>

      {/* Conclusion */}
      {data.conclusionSummary ? (
        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {data.conclusionSummary}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('aiTroubleshooting.report.noConclusion')}
        </Typography>
      )}

      {/* Evidence — omit the block entirely when null/empty (no fabricated rows). */}
      {evidence.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {translate('aiTroubleshooting.report.evidence')}
          </Typography>
          <Table size="small">
            <TableBody>
              {evidence.map((item, idx) => (
                <TableRow key={`${item.kind}-${item.ref}-${idx}`}>
                  <TableCell sx={{ width: 32, verticalAlign: 'top', px: 1 }}>
                    {item.kind === 'db' ? (
                      <DbIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    ) : (
                      <LogIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-word' }}>
                    {item.ref}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                    {item.note}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Muted footer: model / usage / latency. Render "—" when null; never fabricate 0. */}
      <Typography variant="caption" color="text.secondary" component="div">
        {translate('aiTroubleshooting.report.model')}:{' '}
        {data.model && (data.model.provider || data.model.name)
          ? `${data.model.provider ?? '—'}/${data.model.name ?? '—'}`
          : '—'}
        {'  ·  '}
        {translate('aiTroubleshooting.report.tokens')}: {numOrDash(data.usage?.inputTokens)} /{' '}
        {numOrDash(data.usage?.outputTokens)}
        {'  ·  '}
        {translate('aiTroubleshooting.report.cost')}: {costOrDash(data.usage?.costUsd)}
        {'  ·  '}
        {translate('aiTroubleshooting.report.latency')}:{' '}
        {data.latencyMs === null || data.latencyMs === undefined
          ? '—'
          : `${data.latencyMs} ms`}
      </Typography>
    </Box>
  );
};

export default ReportBody;
